import { type StructureDto } from '@/rtkApi/mddApiSliceGenerated'
import { type RootState } from '@/store'
import { type AppDispatch } from '@/store/utils'
import { logError } from '@/utils/errorHandling'
import { hasStructureEditPermission, isEditable } from '@/utils/locking'

import { loadNode } from '../node/general-actions'
import { type TabSession } from '.'
import {
	TAB_CANCEL_EDIT,
	TAB_CHANGE_NAME,
	TAB_CLOSE,
	TAB_CLOSE_CUSTOM,
	TAB_CLOSE_RELATED,
	TAB_EDIT,
	TAB_IMPORT_STATE,
	TAB_OPEN,
	TAB_SELECT,
	TAB_SELECT_CUSTOM,
	TAB_SELECT_TAB,
	TAB_SET_PERMANENT,
	TAB_SET_PROPERTIES_SHOWN,
	TAB_SET_PROPERTIES_WIDTH,
} from './constants'
import { type Actions, type CustomTabProps } from './types'

export const openTab =
	(
		node: StructureDto,
		replace: boolean,
		editMode = false,
		customTabProps?: CustomTabProps,
	) =>
	(dispatch: AppDispatch, getValues: () => RootState): Actions =>
		dispatch({
			type: TAB_OPEN,
			node,
			replace,
			editMode,
			nodes: getValues().node.nodes,
			customTabProps,
		})

export const selectTab = (node: StructureDto): Actions => ({
	type: TAB_SELECT,
	node,
})

export const selectCustomTab = (id: number): Actions => ({
	type: TAB_SELECT_CUSTOM,
	id,
})

export const setTabPermanent = (node: StructureDto): Actions => ({
	type: TAB_SET_PERMANENT,
	node,
})

export const closeTab = (nodeId: number): Actions => ({
	type: TAB_CLOSE,
	nodeId,
})

export const closeCustomTab = (nodeId: number): Actions => ({
	type: TAB_CLOSE_CUSTOM,
	nodeId,
})

export const editTab = (node: StructureDto): Actions => ({
	type: TAB_EDIT,
	node,
})

export const cancelEditingTab = (node: StructureDto): Actions => ({
	type: TAB_CANCEL_EDIT,
	node,
})

export const setTabPropertiesShown = (
	node: StructureDto,
	tabKey: string,
	shown: boolean,
): Actions => ({
	type: TAB_SET_PROPERTIES_SHOWN,
	node,
	tabKey,
	shown,
})

export const setTabPropertiesWidth = (
	node: StructureDto,
	tabKey: string,
	width: number,
): Actions => ({
	type: TAB_SET_PROPERTIES_WIDTH,
	node,
	tabKey,
	width,
})

export const closeTabAndRelated =
	(node: StructureDto) =>
	(dispatch: AppDispatch, getValues: () => RootState): Actions =>
		dispatch({
			type: TAB_CLOSE_RELATED,
			node,
			children: getValues().node.children,
		})

export const loadTabState =
	(session: TabSession) =>
	async (dispatch: AppDispatch, getValues: () => RootState) => {
		const updatedSession = { ...session }
		updatedSession.openedTabs = updatedSession.openedTabs.filter((tab) => !!tab)

		const clampSelected = (tabs: typeof updatedSession.openedTabs) => {
			if (
				updatedSession.selectedTab !== undefined &&
				updatedSession.selectedTab >= tabs.length
			) {
				updatedSession.selectedTab = undefined
			}
			if (
				updatedSession.temporaryTab !== undefined &&
				updatedSession.temporaryTab >= tabs.length
			) {
				updatedSession.temporaryTab = undefined
			}
		}

		const user = getValues().auth.user

		// Custom tabs (e.g. deployment package) use nodeId = release/diagram id. Session stores a
		// stale parentNode snapshot; re-load parent by id and clamp edit flags to current ACL.
		for (const tab of updatedSession.openedTabs) {
			const props = tab.customTabProps
			const parentId = props?.parentNode?.id
			if (props == null || parentId == null) continue

			try {
				let parent = getValues().node.nodes[parentId]
				if (!parent) {
					parent = await dispatch(loadNode(parentId))
				}
				if (!hasStructureEditPermission(parent)) {
					tab.editMode = false
					props.editMode = false
				} else if (tab.editMode && !isEditable(parent, user)) {
					tab.editMode = false
					props.editMode = false
				}
			} catch (error) {
				logError(error, {
					operation: 'loadTabState.clampCustomTabParent',
					details: { parentId },
				})
				tab.editMode = false
				props.editMode = false
			}
		}

		const selectedIndex =
			updatedSession.selectedTab !== undefined &&
			updatedSession.selectedTab < updatedSession.openedTabs.length
				? updatedSession.selectedTab
				: updatedSession.openedTabs.length > 0
					? 0
					: undefined

		if (selectedIndex !== undefined && updatedSession.openedTabs.length > 0) {
			const selectedTab = updatedSession.openedTabs[selectedIndex]
			const nodes = getValues().node.nodes

			if (selectedTab.customTabProps?.customTabName) {
				// Already clamped via parent load above.
			} else {
				const existingNode = nodes[selectedTab.nodeId]
				try {
					const data = existingNode ?? await dispatch(loadNode(selectedTab.nodeId))
					if (selectedTab.editMode && !isEditable(data, user)) {
						selectedTab.editMode = false
					}
				} catch (error) {
					logError(error, {
						operation: 'loadTabState.loadSelectedNode',
						details: { nodeId: selectedTab.nodeId },
					})
					updatedSession.openedTabs = updatedSession.openedTabs.filter(
						(t) => t.nodeId !== selectedTab.nodeId,
					)
					updatedSession.selectedTab = undefined
					updatedSession.temporaryTab = undefined
					clampSelected(updatedSession.openedTabs)
				}
			}
		}

		if (updatedSession.openedTabs.length !== session.openedTabs.length) {
			updatedSession.selectedTab = undefined
			updatedSession.temporaryTab = undefined
		}
		clampSelected(updatedSession.openedTabs)

		dispatch({
			type: TAB_IMPORT_STATE,
			session: updatedSession,
		})
	}

export const selectTabInTab = (nodeId: number, tab: string): Actions => ({
	type: TAB_SELECT_TAB,
	tab,
})

export const changeTabName = (nodeId: number, newName: string): Actions => ({
	type: TAB_CHANGE_NAME,
	nodeId,
	newName,
})
